<template>
  <div class="pt-1">
    <div class="kb-form-fields">
      <div class="kb-form-pair">
        <KbField
          field-id="admin_drawer_first_name"
          label="First name"
          :error="form.errorFor('first_name')"
        >
          <InputText
            id="admin_drawer_first_name"
            v-model="form.values.first_name"
            size="small"
            fluid
            :invalid="Boolean(form.errorFor('first_name'))"
            @blur="form.touch('first_name')"
          />
        </KbField>
        <KbField
          field-id="admin_drawer_last_name"
          label="Last name"
          :error="form.errorFor('last_name')"
        >
          <InputText
            id="admin_drawer_last_name"
            v-model="form.values.last_name"
            size="small"
            fluid
            :invalid="Boolean(form.errorFor('last_name'))"
            @blur="form.touch('last_name')"
          />
        </KbField>
      </div>

      <KbField
        field-id="admin_drawer_birthday"
        label="Date of birth"
        :error="form.errorFor('birthday')"
      >
        <InputText
          id="admin_drawer_birthday"
          v-model="form.values.birthday"
          type="date"
          size="small"
          fluid
          :max="formatDateForInput(get18YearsAgoDate())"
          :invalid="Boolean(form.errorFor('birthday'))"
          @blur="form.touch('birthday')"
        />
      </KbField>

      <KbAddressFields
        v-model="form.values.address"
        id-prefix="admin_drawer_address"
        :error-for="field => form.errorFor(`address.${field}`)"
        @blur="field => form.touch(`address.${field}`)"
      />

      <KbField
        field-id="admin_drawer_email"
        label="Email"
        :hint="isSSOUser ? 'Can\'t be changed for accounts that sign in with Google or Apple' : undefined"
        :error="form.errorFor('email')"
      >
        <InputText
          id="admin_drawer_email"
          v-model="form.values.email"
          type="email"
          size="small"
          fluid
          :disabled="isSSOUser"
          :invalid="Boolean(form.errorFor('email'))"
          @blur="form.touch('email')"
        />
      </KbField>

      <KbField
        field-id="admin_drawer_phone"
        label="Phone"
        :error="form.errorFor('phone')"
      >
        <KbPhoneInput
          id="admin_drawer_phone"
          v-model="form.values.phone"
          :invalid="Boolean(form.errorFor('phone'))"
          @blur="form.touch('phone')"
        />
      </KbField>

      <KbField
        v-if="member.member_type === 'captain'"
        field-id="admin_drawer_hide_phone"
        label="Hide phone number"
        hint="SubKrewe members won't see this captain's phone number"
        inline
      >
        <ToggleSwitch
          v-model="form.values.hide_phone"
          input-id="admin_drawer_hide_phone"
        />
      </KbField>

      <KbField
        v-if="isRedshirt(member)"
        field-id="admin_drawer_tshirt_size"
        label="T-shirt size"
        inline
      >
        <Select
          v-model="form.values.tshirt_size"
          input-id="admin_drawer_tshirt_size"
          :options="TSHIRT_SIZE_OPTIONS"
          option-label="label"
          option-value="value"
          placeholder="Size"
          size="small"
          class="w-28"
        />
      </KbField>
    </div>

    <div class="mt-5">
      <h4 class="kb-label m-0">
        Emergency contacts
      </h4>
      <MemberEmergencyContactsList
        :contacts="member.emergency_contacts || (member.emergency_contact ? [member.emergency_contact] : [])"
        :require-at-least-one="false"
        @update="handleEmergencyContactsUpdate"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { z } from 'zod'
import type { Member } from '~/shared/types/member'
import { isRedshirt } from '~/shared/types/memberType.js'
import { TSHIRT_SIZE_OPTIONS } from '~/shared/types/tshirtSize.js'
import { formatDateForInput, get18YearsAgoDate } from '~/utils/dateUtils'
import { addressSchema, addressValues, emailField, optionalPhoneField, requiredDate, requiredText } from '~/utils/formSchemas'

interface Props {
  member: Member
}

const props = defineProps<Props>()
const emit = defineEmits(['update', 'update:title', 'save'])

const membersStore = useMembersStore()
const toast = useToast()

// Check if member uses SSO (Google or Apple)
const isSSOUser = ref(false)

async function checkAuthProvider() {
  try {
    const data = await $fetch<{ loginMethods: string[] }>('/api/admin/auth/user-info', {
      query: { uid: props.member.id },
    })
    isSSOUser.value = data.loginMethods.some(m => m.includes('SSO'))
  }
  catch {
    // If we can't fetch auth info, assume not SSO (allow editing)
    isSSOUser.value = false
  }
}

onMounted(() => {
  checkAuthProvider()
})

watch(() => props.member.id, () => {
  checkAuthProvider()
})

const schema = z.object({
  first_name: requiredText('First name is required'),
  last_name: requiredText('Last name is required'),
  birthday: requiredDate('Date of birth is required'),
  address: addressSchema,
  email: emailField,
  phone: optionalPhoneField,
  hide_phone: z.boolean(),
  tshirt_size: z.string().optional(),
})

function valuesFrom(member: Member): z.input<typeof schema> {
  return {
    first_name: member.first_name || '',
    last_name: member.last_name || '',
    birthday: formatDateForInput(member.birthday) || '',
    address: addressValues(member.address),
    email: member.email || '',
    phone: member.phone || '',
    hide_phone: Boolean(member.hide_phone),
    tshirt_size: member.tshirt_size || undefined,
  }
}

const form = useZodForm(schema, valuesFrom(props.member))

// The record changed underneath (a save landing, another admin): take it in
// unless someone is mid-edit here
watch(() => props.member, (member) => {
  if (!form.isDirty.value) form.reset(valuesFrom(member))
}, { deep: true })

// Auto-save: one second after the last change, save the fields that changed,
// as long as those fields are valid. Other fields with old, incomplete data
// (a missing zip, say) don't block saving the field being edited.
let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null
const clearAutoSave = () => {
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout)
    autoSaveTimeout = null
  }
}

watch(() => form.values, () => {
  if (!form.isDirty.value) return
  clearAutoSave()
  const memberId = props.member.id
  autoSaveTimeout = setTimeout(() => {
    if (memberId === props.member.id) save()
  }, 1000)
}, { deep: true })

const saving = ref(false)

async function save() {
  if (saving.value || !form.isDirty.value) return
  const changed = form.changedValues()
  const fields = Object.keys(changed)
  for (const field of fields) form.touched[field] = true
  if (!form.validateFields(...fields)) return

  saving.value = true
  try {
    // Always get the canonical member from store to avoid object reference issues
    const memberPath = `members/${props.member.id}`
    const canonicalMember = membersStore.members[memberPath] || props.member

    const updates: Partial<Member> = { ...(changed as Partial<Member>) }
    if ('first_name' in changed || 'last_name' in changed) {
      updates.name = `${form.values.first_name} ${form.values.last_name}`.trim()
      if (updates.name !== canonicalMember.name) emit('update:title', updates.name || 'Member Details')
    }

    const savedValues = JSON.stringify(form.values)
    const success = await membersStore.updateMember(canonicalMember, updates)

    if (success) {
      // Only what was sent counts as saved; typing that happened meanwhile
      // stays dirty and saves on the next round
      if (JSON.stringify(form.values) === savedValues) form.markSaved()
      toast.add({
        severity: 'success',
        summary: 'Saved',
        detail: `Member ${updates.name ?? canonicalMember.name} updated`,
        life: 2000,
      })
      emit('save', updates)
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update member information',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    saving.value = false
  }
}

// Handle emergency contacts updates from the list component
const handleEmergencyContactsUpdate = async (contacts: Array<{ name: string, phone: string, relationship?: string }>) => {
  try {
    const memberPath = `members/${props.member.id}`
    const canonicalMember = membersStore.members[memberPath] || props.member

    const success = await membersStore.updateMember(canonicalMember, {
      emergency_contacts: contacts,
      // The legacy field has no `relationship` and is optional rather than
      // nullable, so absent means absent.
      emergency_contact: contacts[0]
        ? { name: contacts[0].name, phone: contacts[0].phone }
        : undefined,
    })

    if (!success) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update emergency contacts',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
}

// Clean up on unmount
onUnmounted(() => {
  clearAutoSave()
})
</script>
